import React from 'react';
import { motion } from 'framer-motion';
import {
  Eye,
  MoreVertical,
  ArrowRight,
  Ship,
  Package,
  Box,
  Scale,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  COLUMN_ACTIONS,
  ICON_MAP,
  getPrimaryAction,
  isActionEnabled,
} from './columns/lclColumnActions';
import {
  shortId,
  formatCodigo,
  formatCliente,
  formatClienteNit,
  formatRuta,
  formatRutaSub,
} from '../../utils/lclFormatters';

/**
 * Tarjeta de solicitud LCL para el tablero Kanban.
 *
 * Diferencias con la tarjeta aérea:
 *  - Icono `Ship` en vez de `Plane`.
 *  - Muestra **CBM (m³) y W/M**, que es lo que determina el precio en
 *    marítimo, en lugar del peso volumétrico.
 *  - La ruta usa UN/LOCODE de puertos en vez de IATA de aeropuertos.
 *
 * Props:
 *  - r: registro de la solicitud LCL
 *  - variant: 'cotizaciones' | 'en-curso' | 'finalizadas' | 'rechazadas'
 *  - onAction: (actionKey, r) => void
 *  - actionsMode: 'full' | 'view' (default 'full')
 *      - 'full' → acción primaria + menú ⋮
 *      - 'view' → sin acciones; solo abre el detalle al hacer click
 */

const VARIANT_BADGE = {
  cotizaciones: 'bg-amber-100 text-amber-800 border-amber-200',
  'en-curso': 'bg-blue-100 text-blue-800 border-blue-200',
  finalizadas: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rechazadas: 'bg-rose-100 text-rose-800 border-rose-200',
};

const VARIANT_ACCENT = {
  cotizaciones: 'before:bg-amber-400',
  'en-curso': 'before:bg-blue-500',
  finalizadas: 'before:bg-emerald-500',
  rechazadas: 'before:bg-rose-500',
};

const LclOperationCard = ({ r, variant = 'cotizaciones', onAction, actionsMode = 'full' }) => {
  const codigo = formatCodigo(r);
  const estado = r?.estado || '—';
  const cliente = formatCliente(r);
  const clienteNit = formatClienteNit(r);
  const ruta = formatRuta(r);
  const rutaSub = formatRutaSub(r);
  const incoterm = r?.incotermSnapshot?.codigo || '';
  const tipo = r?.tipoOperacion || '';
  const numPiezas = r?.numPiezas;
  const volumenTotal = r?.volumenTotal;
  const unidadesWM = r?.unidadesWM;

  const showActions = actionsMode !== 'view';
  const actions = showActions ? COLUMN_ACTIONS[variant] || [] : [];
  const primary = showActions ? getPrimaryAction(variant) : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      whileHover={{ y: -2 }}
      className={cn(
        'group relative rounded-lg border bg-card shadow-sm hover:shadow-md transition-all cursor-pointer',
        'before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:rounded-l-lg',
        VARIANT_ACCENT[variant],
      )}
      onClick={() => onAction && onAction('view', r)}
    >
      <div className="p-3 pl-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <Ship className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="font-mono text-xs font-semibold text-foreground truncate">
                {codigo}
              </span>
            </div>
            <p
              className="text-[10px] text-muted-foreground font-mono mt-0.5 truncate"
              title={r?._id}
            >
              {shortId(r?._id)}
            </p>
          </div>
          <span
            className={cn(
              'text-[10px] font-semibold px-1.5 py-0.5 rounded-full border shrink-0',
              VARIANT_BADGE[variant],
            )}
          >
            {estado}
          </span>
        </div>

        <p className="text-sm font-semibold text-foreground truncate" title={cliente}>
          {cliente}
        </p>
        {clienteNit && (
          <p className="text-[10px] text-muted-foreground -mt-1.5 truncate">
            NIT {clienteNit}
          </p>
        )}

        <div className="space-y-0.5">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="truncate flex-1" title={ruta}>
              {ruta}
            </span>
            <ArrowRight className="h-3 w-3 shrink-0" />
            <span className="truncate flex-1 text-right invisible">·</span>
          </div>
          {rutaSub && (
            <p className="text-[10px] text-muted-foreground truncate" title={rutaSub}>
              {rutaSub}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 text-[11px] text-muted-foreground pt-1 border-t border-border/50 flex-wrap">
          {tipo && (
            <span className="capitalize truncate" title={tipo}>
              {tipo}
            </span>
          )}
          {incoterm && (
            <span className="font-mono text-[10px] px-1 py-0.5 bg-muted rounded">
              {incoterm}
            </span>
          )}
          {numPiezas != null && (
            <span className="flex items-center gap-0.5 shrink-0" title="Bultos">
              <Package className="h-3 w-3" />
              {numPiezas}
            </span>
          )}
          {volumenTotal != null && (
            <span className="flex items-center gap-0.5 shrink-0" title="Volumen (CBM)">
              <Box className="h-3 w-3" />
              {Number(volumenTotal).toFixed(3)} m³
            </span>
          )}
          {unidadesWM != null && (
            <span
              className="flex items-center gap-0.5 shrink-0 font-medium text-foreground/70"
              title="Unidades facturables (weight or measurement)"
            >
              <Scale className="h-3 w-3" />
              {Number(unidadesWM).toFixed(3)} W/M
            </span>
          )}
        </div>

        {showActions && (
          <div
            className="flex items-center justify-end gap-1 pt-1 -mb-1"
            onClick={(e) => e.stopPropagation()}
          >
            {primary && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[11px] text-primary hover:bg-primary/10"
                onClick={() => onAction && onAction(primary.key, r)}
                disabled={!isActionEnabled(primary, r)}
              >
                {primary.label}
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  aria-label="Más acciones"
                >
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {actions.map((a) => {
                  if (a.key === 'view') return null;
                  const Icon = ICON_MAP[a.icon] || Eye;
                  const disabled = !isActionEnabled(a, r);
                  return (
                    <DropdownMenuItem
                      key={a.key}
                      onClick={() => onAction && onAction(a.key, r)}
                      disabled={disabled}
                      className="cursor-pointer"
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      <span>{a.label}</span>
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onAction && onAction('view', r)}
                  className="cursor-pointer"
                >
                  <Eye className="mr-2 h-4 w-4" />
                  <span>Ver detalles</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default LclOperationCard;
